import { modalActions, placeActions } from '@/actions';
import { Button } from '@/components/elements/forms/buttons';
import HR from '@/components/elements/HR/HR';
import { LoadingIcon } from '@/components/icons';
import Icon from '@/components/icons/Icon';
import BannerSlot from '@/components/modules/banner/BannerSlot';
import Breadcrumbs from '@/components/modules/breadcrumbs/Breadcrumbs';
import { MapTiler } from '@/components/modules/map';
import { DynamicMapProps } from '@/components/modules/map/MapTiler';
import { AndroidTopBanner, IPhoneTopBanner } from '@/components/modules/mobile';
import BookAppointmentModal from '@/components/modules/modals/BookAppointment/BookAppointment';
import UserLocationOnboarding from '@/components/modules/modals/UserLocationOnboarding';
import BottomNavBar from '@/components/modules/navigation/BottomNavBar/BottomNavBar';
import NavBar from '@/components/modules/navigation/navbar/NavBar/NavBar';
import NavigationDrawer from '@/components/modules/navigation/NavigationDrawer/NavigationDrawer';
import { SERPTopInfoBanner } from '@/components/modules/pages/serp';
import Pagination from '@/components/modules/pages/serp/Pagination';
import { PlaceCardContainer } from '@/components/modules/place';
import PlaceCardSkeleton from '@/components/skeletons/PlaceCardSkeleton';
import { PLACE_SUBSCRIPTION_TYPE } from '@/constants';
import { BANNER_SLOT_LOCATION } from '@/constants/banner';
import { Footer } from '@/features/footer';
import MegaMenu from '@/features/mega-menu/MegaMenu';
import { useMegaMenuContext } from '@/features/mega-menu/MegaMenu.hooks';
import { HideOnScrollWrapper } from '@/features/search-header/SearchHeader';
import {
  classnames,
  getSERPBreadcrumbs,
  setBookingStartingPoint,
  showAndroidTopBanner,
  showIphoneTopBanner,
  trackMpMetaEvent,
  trackPage,
} from '@/helpers';
import { trackMpEvent } from '@/helpers/analyticsTS';
import { getSearchResultsPageHeadingProps } from '@/helpers/seoTs';
import { locationSlug } from '@/helpers/urlTs';
import { useAppSelector } from '@/hooks';
import { useSearchContext } from '@/hooks/search/useSearch';
import { DEFAULT_MY_LOCATION_KEYWORD, SEARCH_PAGE_VIEWS } from '@/hooks/search/useSearch.constants';
import { useSearchStateCache } from '@/hooks/search/useSearchStateCache';
import { useHandlePopstate } from '@/hooks/useHandlePopState';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import useMobileView from '@/hooks/useMobileView';
import { _s, _t } from '@/locale';
import { SEARCH_ACTION } from '@/reducers/searchV2Reducer';
import { placeService, PLACE_IMPRESSION } from '@/services';
import { Tracking } from '@/types/analytics';
import { SearchPlace } from '@/types/api/services/search';
import { Fragment, MouseEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import useKeywordAutoSuggestManager from '../components/AutoSuggest/KeywordAutoSuggest/KeywordAutoSuggest.hooks';
import { KeywordSelection } from '../components/AutoSuggest/KeywordAutoSuggest/KeywordAutoSuggest.type';
import useLocationAutoSuggestManager from '../components/AutoSuggest/LocationAutoSuggest/LocationAutoSuggest.hooks';
import useDateTimePickerManager from '../components/DateTimePicker/DateTimePicker.hooks';
import { DateTimePickerState } from '../components/DateTimePicker/DateTimePicker.types';
import useFilterManager from '../components/Filter/Filter.hooks';
import { MatchingSearchResultsCount } from '../components/MatchingSearchResultsCount';
import useSortingManager from '../components/Sorting/Sorting.hooks';
import { SEARCH_RESULT_PER_PAGE_LIMIT } from '../constants';
import { getPageScreenName, trackPlaceClick } from '../helpers/tracking';
import NoSearchHits from '../modules/NoSearchHits';
import SearchControls, { SearchControlProps } from '../modules/SearchControls/SearchControls';
import useSearchControlsManager from '../modules/SearchControls/SearchControls.hooks';
import SearchForm from '../modules/SearchForm/SearchForm';
import SEO from '../modules/SEO';
import { getSearchParamsForMatchedServices, hasNextPage } from './index.helpers';

export const baseTranslationKey = 'features.searchV2.pages.SearchResultsPage';

const MAP_BASIS = 'basis-[40%]';
const LIST_BASIS = 'basis-[60%]';

const SearchResultsPage = () => {
  const hasFetchedOnce = useRef(false);
  const searchManager = useSearchContext();
  const searchStateCache = useSearchStateCache();

  const flags = useAppSelector((state) => state.flags);
  const searchV2 = useAppSelector((state) => state.searchV2);
  const modal = useAppSelector((state) => state.modal);
  const dispatch = useDispatch();
  const { isMobileView } = useMobileView();
  const popstate = useHandlePopstate();
  const navigation = useLocation();
  const megaMenuManager = useMegaMenuContext();
  const { show: showNavigationDrawer = false } = useAppSelector((state) => state?.navigationDrawer) as {
    show: boolean;
  };

  const searchControlsScrollRef = useRef<HTMLDivElement>(null);
  const searchControlsIntersectionObserver = useIntersectionObserver(searchControlsScrollRef, { threshold: 1 });

  const { q, location, page, prefs, startDate, endDate, timeOfDay, paginate, type, viewToggle } = searchManager;
  const {
    places = [],
    profilePages = [],
    fetching,
    fetchingExtendedPlaces,
    topSearch,
    showInjectionNotice,
    results,
    extendSearchDisabled,
    bounds,
  } = searchV2;

  const premiumProfilePlaces = profilePages.filter(
    (place) => place.settings?.subscriptionType === PLACE_SUBSCRIPTION_TYPE.PROFILE_PAGE_PREMIUM,
  );
  const verifiedProfilePlaces = profilePages.filter(
    (place) => place.settings?.subscriptionType === PLACE_SUBSCRIPTION_TYPE.PROFILE_PAGE_VERIFIED,
  );

  const keywordAutoSuggestManager = useKeywordAutoSuggestManager({
    initialState: {
      keyword: q,
      suggestions: [],
      storedSuggestions: [],
      newServices: [],
      hasHistory: false,
      showSuggestions: true,
    },
    searchType: searchManager.type,
    onSelectKeywordSuggestion: handleOnSelectKeywordSuggestion,
  });

  const locationAutoSuggestManager = useLocationAutoSuggestManager({
    initialState: {
      locationValue: location,
      hasHistory: false,
      suggestions: [],
      storedSuggestions: [],
      showSuggestions: true,
      isFetchingUserPosition: false,
    },
    loadFromHistory: type !== 'sistaminuten',
    onSelectLocationSuggestion: handleOnUpdateLocation,
  });

  const searchControlManager = useSearchControlsManager(searchManager);
  const { onChangeAppliedFilters, onChangeSorting, onClearSorting } = searchControlManager;

  const filterManager = useFilterManager({
    initialState: { prefs, showOptions: false },
    urlPrefs: prefs,
    applyOnToggle: !isMobileView,
    onChangeAppliedFilters,
  });

  const sortingManager = useSortingManager({
    initialState: { sort: searchManager.sort, showOptions: false },
    urlSorting: searchManager.sort,
    onChangeSorting,
    onClearSorting,
  });

  // pending flag to bridge debounce before debounced search sets `fetching` flag
  const [pendingSearch, setPendingSearch] = useState(!searchV2.isSSRFetched);
  const performSearch = useCallback<typeof searchManager.performSearch>(
    (params, options) => {
      if (searchStateCache.isRestoring) {
        setPendingSearch(false);
        return;
      }

      setPendingSearch(true);
      return searchManager.performSearch(params, options, () => setPendingSearch(false));
    },
    [searchManager],
  );
  const performSyncedSearch = useCallback<typeof searchManager.performSyncedSearch>(() => {
    if (searchStateCache.isRestoring) {
      setPendingSearch(false);
      return;
    }

    setPendingSearch(true);
    return searchManager.performSyncedSearch();
  }, [searchManager]);

  const managerWithPending = useMemo(() => ({ ...searchManager, performSearch }), [searchManager, performSearch]);
  const showSkeleton = (fetching || pendingSearch) && !searchStateCache.isRestoring;

  const searchControlsProps: SearchControlProps = {
    ...searchControlManager,
    searchManager,
    sortingManager,
    filterManager,
    showSistaminutenToggle: false,
  };

  const dateTimePickerManager = useDateTimePickerManager({
    initialState: {
      startDate: startDate,
      endDate: endDate,
      timeOfDay: timeOfDay,
      showCalendar: false,
    },
    onUpdateDateTime: handleOnUpdateDateTime,
    onClearDateTime: handleOnClearDateTime,
  });

  const isSistaminutenSearch = type === 'sistaminuten';
  const hasVerifiedProfilePlaces = verifiedProfilePlaces?.length > 0;
  const noMatchingSearchResults = Boolean(!places[page]?.length && !showSkeleton) && !profilePages?.length;
  const topOfSearchPlaces = page < 1 ? topSearch?.places ?? [] : [];
  const topPlacesEntries = !page && topSearch && topSearch.places ? topSearch.places.length : 0;
  const serpPageNo = page ? +page + 1 : 1;
  const source = prefs?.indexOf('5') !== -1 ? 'serp-page-top' : 'serp-page';
  const breadcrumbs = getSERPBreadcrumbs(q, location, type);
  const heading = getSearchResultsPageHeadingProps(searchManager);
  const showUserLocationOnboarding = !modal.login?.show && modal.userLocationOnboarding?.show;
  const pageScreenName = getPageScreenName(type, viewToggle.view, isMobileView);
  const hasNextPageResults = hasNextPage(results, searchManager.page || 0, SEARCH_RESULT_PER_PAGE_LIMIT);
  const canExtendCurrentPage = places[page]?.length <= SEARCH_RESULT_PER_PAGE_LIMIT;
  const shouldRenderExtendedSearch = Boolean(
    !extendSearchDisabled && searchManager.location && !fetching && canExtendCurrentPage && !bounds,
  );

  // Near me: open the map on the user and the closest results, not on sponsored or profile-page
  // places that can be anywhere in the country.
  const isNearMeSearch = locationSlug(searchManager.location || '') === DEFAULT_MY_LOCATION_KEYWORD;

  // A desktop popup keeps the click handler it was drawn with for as long as its place stays on the map, across
  // searches, so the click reads the current results through this ref.
  const mapPlaceClickRef = useRef<(place: SearchPlace) => void>();
  mapPlaceClickRef.current = (place: SearchPlace) => {
    const screenName: Tracking.ScreenName = searchV2.isSistaminutenSearch
      ? 'smt_search_results_map'
      : 'search_results_map';
    // The map's own carousel index only follows the mobile carousel; a desktop popup click reports a stale one.
    // Profile-page places aren't in the search's result_ids, so a click on one sends no position.
    const position = [...topOfSearchPlaces, ...(places[page] || [])].findIndex(({ id }) => id === place.id);
    trackPlaceClick(searchManager, place, position < 0 ? undefined : position, screenName, false, 'serp_map');
    setBookingStartingPoint(searchV2.isSistaminutenSearch ? 'smt_search_results' : 'search_results', searchManager.q);
    placeService.storePlaceImpressions([place.id], PLACE_IMPRESSION.SERP_TO_PLACE_CLICK);
  };
  const handleMapPlaceClick = useCallback((place: SearchPlace) => mapPlaceClickRef.current?.(place), []);

  const mapPlaces = [
    ...(topOfSearchPlaces || []),
    ...(places[page] || []),
    ...(hasNextPageResults ? [] : profilePages),
  ];

  const mapProps: DynamicMapProps = {
    places: mapPlaces,
    ...(isNearMeSearch && { fitPlaces: places[page] || [], fitToPosition: true }),
    fetching: Boolean(fetching || fetchingExtendedPlaces),
    position: (searchManager.lat && searchManager.lon ? `${searchManager.lat}-${searchManager.lon}` : false) as
      | string
      | false,
    onMapBoundsChange: handleOnMapBoundsChange,
    columnClass: MAP_BASIS,
    bounds: searchV2.bounds,
    boundsFit: searchV2.boundsFit,
    debugLocation: searchV2.debugLocation,
    isSistaminutenSearch: searchV2.isSistaminutenSearch,
    onPlaceClick: handleMapPlaceClick,
  };

  const topBannerConfig = flags?.[BANNER_SLOT_LOCATION.TOP_BANNER_SEARCH]?.payload;
  const middleBannerMobileConfig =
    isMobileView &&
    results > 0 &&
    (flags?.[BANNER_SLOT_LOCATION.MIDDLE_BANNER_SEARCH]?.payload ||
      flags?.[BANNER_SLOT_LOCATION.MIDDLE_BANNER_SEARCH_WEB]?.payload);
  const middleBannerMobileLocationId = flags?.[BANNER_SLOT_LOCATION.MIDDLE_BANNER_SEARCH]?.payload
    ? BANNER_SLOT_LOCATION.MIDDLE_BANNER_SEARCH
    : BANNER_SLOT_LOCATION.MIDDLE_BANNER_SEARCH_WEB;
  const middleBannerDesktopConfig = !isMobileView && flags?.[BANNER_SLOT_LOCATION.MIDDLE_BANNER_SEARCH_WEB]?.payload;

  function handleOnClearDateTime() {
    performSearch({ startDate: null, endDate: null, timeOfDay: null });
  }

  function handleOnUpdateDateTime(state: DateTimePickerState) {
    dateTimePickerManager.onCloseCalendar();
    performSearch({ startDate: state.startDate, endDate: state.endDate, timeOfDay: state.timeOfDay });
  }

  function handleOnUpdateLocation({ searchContext, ...params }: any) {
    performSearch(
      { ...params, q: keywordAutoSuggestManager.keyword },
      {
        bounds: null,
        keywordSubmitted: keywordAutoSuggestManager.keyword !== searchManager.q || undefined,
        searchContext,
      },
    );
  }

  function handleOnSelectKeywordSuggestion(suggestion: KeywordSelection) {
    if (locationAutoSuggestManager.location) {
      keywordAutoSuggestManager.saveKeywordToHistory(suggestion.keyword);
      performSearch(
        { location: locationAutoSuggestManager.location, q: suggestion.keyword },
        { keywordSubmitted: true, searchContext: 'search_autocomplete' },
      );
    }
  }

  function handleOnRequestExtendedSearchClick() {
    searchManager.performSearch({}, { extendedSearch: true });
  }

  function handleOnRequestUserLocation() {
    dispatch(modalActions.userLocationOnboarding({ show: false }));
    locationAutoSuggestManager.onFetchAndSetUserLocation(DEFAULT_MY_LOCATION_KEYWORD);
  }

  function handleOnClickPlace(place: SearchPlace, serpIndex: number | undefined, isTopOfSearch = false) {
    searchStateCache.saveSearchState(searchV2);
    trackPlaceClick(searchManager, place, serpIndex, pageScreenName, isTopOfSearch, 'serp');
    setBookingStartingPoint(pageScreenName, searchManager.q);
    placeService.storePlaceImpressions([place.id], PLACE_IMPRESSION.SERP_TO_PLACE_CLICK);
  }

  function handleNextPageClick(e: MouseEvent<HTMLAnchorElement>) {
    if (!hasNextPageResults) return;
    setPendingSearch(true);
    paginate({ onCachedResults: () => setPendingSearch(false) }).nextPage();
    window.scrollTo(0, 0);
    e.preventDefault();
  }

  function handlePrevPageClick(e: MouseEvent<HTMLAnchorElement>) {
    if (page <= 0) return;
    setPendingSearch(true);
    paginate({ onCachedResults: () => setPendingSearch(false) }).prevPage();
    window.scrollTo(0, 0);
    e.preventDefault();
  }

  function handleOnMapBoundsChange(bounds: string) {
    performSearch({ page: 0 }, { bounds });
  }

  const prepend = showIphoneTopBanner() ? (
    <IPhoneTopBanner source={pageScreenName} />
  ) : showAndroidTopBanner() ? (
    <AndroidTopBanner source={pageScreenName} />
  ) : null;

  useEffect(() => {
    // switch to list view if not on mobile
    if (!isMobileView && viewToggle.view === 'map') {
      viewToggle.setView(SEARCH_PAGE_VIEWS[0]);
    }
  }, [isMobileView, viewToggle]);

  // restore state on page load
  useEffect(() => {
    const restoredState = searchStateCache.tryRestoreSearchState();

    if (restoredState) {
      setPendingSearch(false);

      dispatch({
        type: SEARCH_ACTION.SET_RESTORE_STATE,
        payload: restoredState,
      });

      // Complete restoration after sync effect has had a chance to check isRestoring flag
      queueMicrotask(() => {
        searchStateCache.completeRestoration();
      });
    }
  }, [searchStateCache, dispatch]);

  useEffect(
    () => {
      /**
       * If the url has been updated by the browser navigation
       * change the input values to match the url and perform a new search
       * to sync the search results with the url
       */
      if (searchStateCache.isRestoring) {
        return;
      }

      let shouldSyncSearchResults = false;

      if ((popstate.didUpdate || navigation) && keywordAutoSuggestManager.keyword !== q) {
        keywordAutoSuggestManager.onChange(null, { newValue: q });
        shouldSyncSearchResults = true;
      }

      if ((popstate.didUpdate || navigation) && locationAutoSuggestManager.location !== location) {
        locationAutoSuggestManager.onChange({ target: { value: location } });
        shouldSyncSearchResults = true;
      }

      if ((popstate.didUpdate || navigation) && searchV2.isSistaminutenSearch !== isSistaminutenSearch) {
        shouldSyncSearchResults = true;
      }

      if (shouldSyncSearchResults) {
        performSyncedSearch();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [popstate.didUpdate, navigation, searchStateCache.isRestoring],
  );

  useEffect(() => {
    if (fetching && !searchStateCache.isRestoring) {
      searchStateCache.clearSearchState();
      window.scrollTo(0, 0);
      hasFetchedOnce.current = true;
    }
    if (!fetching && hasFetchedOnce.current) setPendingSearch(false);
  }, [fetching]);

  useEffect(() => {
    const search_query = q.trim?.().length > 0 ? q : undefined;
    const screen_name: Tracking.ScreenName = isSistaminutenSearch ? 'smt_search_results' : 'search_results';

    trackMpEvent(
      {
        name: 'screen_shown',
        properties: { screen_name },
      },
      { sendToCio: false },
    );

    trackPage();

    if (search_query) {
      trackMpMetaEvent('Search', { search_string: search_query });
    }
  }, []);

  const getHitsWithProfilePlaces = (results: number) => {
    let resultsIncProfilePlaces = results;
    if (premiumProfilePlaces.length) {
      resultsIncProfilePlaces += premiumProfilePlaces.length;
    }
    return resultsIncProfilePlaces;
  };

  const View: {
    [key in (typeof SEARCH_PAGE_VIEWS)[number]['view']]: ReactNode;
  } = {
    list: (
      <div className="justify-end md:flex">
        <div
          className={classnames(
            LIST_BASIS,
            /**
             *  ### SPLIT AREA (list + map) styles ###
             *  we fake the same horizontal alignment as the 1440px container (container-layout)
             *  which the Mega menu navigation uses to align list + map content with the navigation.
             *  calc((100vw-1440px)/2) gives left gutter when >1440px.
             *  fallback 24px on smaller screens to keep the left padding as container-layout 24px.
             */
            !isMobileView && classnames('pl-[max(calc((100vw-1440px)/2),24px)]'),
          )}>
          {showInjectionNotice && q.length > 0 && <SERPTopInfoBanner variant="injectionInfo" important />}
          {topBannerConfig && (
            <BannerSlot config={topBannerConfig} locationId={BANNER_SLOT_LOCATION.TOP_BANNER_SEARCH} />
          )}
          <div className={classnames('container-screen', !isMobileView && 'relative')}>
            <div className="space-y-md md:space-y-xl pb-md md:pb-lg">
              <Breadcrumbs items={breadcrumbs} className="text-sm md:text-base" />
              {isSistaminutenSearch && (
                <div className="bg-bg_secondary_bold p-xs  gap-x-xs flex items-center rounded-sm">
                  <Icon variant="smt-filled" style={{ filter: 'var(--filter-color-bg-accent-5)' }} />
                  <span className="font-semibold">
                    Sista Minuten -{' '}
                    <span className="font-normal">{_s(`${baseTranslationKey}.alwaysWithDiscount`)}</span>
                  </span>
                </div>
              )}
              <h1 className="text-h-m md:text-h-l">
                <span className="text-fg_primary font-semibold">
                  {heading.keyword}
                  {isSistaminutenSearch ? <>&nbsp;{_s(`${baseTranslationKey}.withDiscount`)},</> : <>,</>}
                </span>
                <span className="text-fg_secondary"> {heading.location.replace(' ', '\u00A0')}</span>
              </h1>
              {heading.description && <p className="text-fg_secondary text-l">{heading.description}</p>}
              <div ref={searchControlsScrollRef} className="gap-x-sm flex items-start">
                <SearchControls {...searchControlsProps} />
                {isMobileView && (
                  <>
                    {viewToggle.render}
                    {/* just a spacer for horizontal scroll */}
                    <div className="pl-sm"></div>
                  </>
                )}
              </div>

              <span className="space-x-xs border-border_regular pt-md flex items-center border-t">
                <MatchingSearchResultsCount
                  {...searchManager}
                  results={getHitsWithProfilePlaces(searchManager.results)}
                  loading={showSkeleton}
                />
              </span>

              {noMatchingSearchResults && !showSkeleton && (
                <NoSearchHits
                  isSistaminutenSearch={isSistaminutenSearch}
                  keyword={q}
                  location={location}
                  fetchingExtendedPlaces={fetchingExtendedPlaces}
                  onExtendSearchClick={handleOnRequestExtendedSearchClick}
                />
              )}
            </div>

            {middleBannerMobileConfig && (
              <div className="py-md">
                <BannerSlot config={middleBannerMobileConfig} locationId={middleBannerMobileLocationId} />
              </div>
            )}
            {middleBannerDesktopConfig && (
              <div className="py-lg">
                <BannerSlot
                  config={middleBannerDesktopConfig}
                  locationId={BANNER_SLOT_LOCATION.MIDDLE_BANNER_SEARCH_WEB}
                  inContainer={false}
                />
              </div>
            )}
            <div className="space-y-md">
              {showSkeleton && (
                <div role="status" aria-live="polite" className="space-y-md">
                  {Array.from({ length: isMobileView ? 3 : 6 }).map((_, i) => (
                    <PlaceCardSkeleton key={`serp-loading-${i}`} />
                  ))}
                  <span className="sr-only">{_t(`${baseTranslationKey}.loadingSearchResults`)}</span>
                </div>
              )}
              {topOfSearchPlaces?.map((place, key) => {
                const serpIndex = key;
                return (
                  <PlaceCardContainer
                    source="serp-page-top"
                    place={place}
                    key={serpIndex}
                    SerpPageNo={serpPageNo}
                    SerpIdx={serpIndex}
                    onClickPlace={() => handleOnClickPlace(place, serpIndex, true)}
                    fireHover={() => dispatch(placeActions.setHover(place.id))}
                    fireHoverOut={() => dispatch(placeActions.removeHover())}
                    searchParams={getSearchParamsForMatchedServices({ ...searchManager })}
                  />
                );
              })}
              {places?.[page]?.map((place, key) => {
                const serpIndex = key + topPlacesEntries;
                return (
                  <PlaceCardContainer
                    source={source}
                    place={place}
                    key={serpIndex}
                    SerpPageNo={serpPageNo}
                    SerpIdx={serpIndex}
                    onClickPlace={() => handleOnClickPlace(place, serpIndex, false)}
                    fireHover={() => dispatch(placeActions.setHover(place.id))}
                    fireHoverOut={() => dispatch(placeActions.removeHover())}
                    searchParams={getSearchParamsForMatchedServices({ ...searchManager })}
                    isSistaminutenSearch={isSistaminutenSearch}
                  />
                );
              })}
              {!hasNextPageResults &&
                premiumProfilePlaces.map((place, key) => {
                  const serpIndex = key;
                  return (
                    <PlaceCardContainer
                      source={'profilePages'}
                      place={place}
                      key={serpIndex}
                      SerpPageNo={serpPageNo}
                      SerpIdx={serpIndex}
                      // Profile-page places aren't in the search's result_ids, so their clicks send no position.
                      onClickPlace={() => handleOnClickPlace(place, undefined, false)}
                      fireHover={() => dispatch(placeActions.setHover(place.id))}
                      fireHoverOut={() => dispatch(placeActions.removeHover())}
                      searchParams={getSearchParamsForMatchedServices({ ...searchManager })}
                    />
                  );
                })}
            </div>
          </div>

          {hasVerifiedProfilePlaces && !hasNextPageResults && (
            <>
              <div className="py-md px-xl flex">
                <div className="px-lg flex-1">
                  <HR className="bg-fg_secondary"></HR>
                </div>
                <div className="text-center">
                  <h2 className="text-h-s text-fg_primary">
                    {location
                      ? _s(`${baseTranslationKey}.relatedCompanies.title`, { location: location })
                      : _s(`${baseTranslationKey}.relatedCompanies.titleWithoutLocation`)}
                  </h2>
                  <span className="text-s text-fg_secondary">
                    {_s(`${baseTranslationKey}.relatedCompanies.description`)}
                  </span>
                </div>

                <div className="px-lg flex-1">
                  <HR className="bg-fg_secondary"></HR>
                </div>
              </div>
              <div className="px-md gap-md flex flex-col">
                {verifiedProfilePlaces.map((place, key) => {
                  const serpIndex = key;
                  return (
                    <PlaceCardContainer
                      source={'profilePages'}
                      place={place}
                      key={serpIndex}
                      SerpPageNo={serpPageNo}
                      SerpIdx={serpIndex}
                      // Profile-page places aren't in the search's result_ids, so their clicks send no position.
                      onClickPlace={() => handleOnClickPlace(place, undefined, false)}
                      fireHover={() => dispatch(placeActions.setHover(place.id))}
                      fireHoverOut={() => dispatch(placeActions.removeHover())}
                      searchParams={getSearchParamsForMatchedServices({ ...searchManager })}
                    />
                  );
                })}
              </div>
            </>
          )}

          <div className="container-screen">
            <div className="pb-lg">
              {shouldRenderExtendedSearch && !noMatchingSearchResults && !hasNextPageResults && !showSkeleton && (
                <div className="p-md w-full text-center">
                  <p className="text-xl">{_s('extendSearchTitle')}</p>
                  <p className="text-l text-black-600">{_s('extendSearchSubTitle')}</p>
                  <Button
                    className="mt-xl"
                    disabled={fetchingExtendedPlaces}
                    onClick={handleOnRequestExtendedSearchClick}>
                    <span className="grid items-center" style={{ gridTemplateAreas: 'stack' }}>
                      <span className={fetchingExtendedPlaces ? 'invisible' : 'visible'} style={{ gridArea: 'stack' }}>
                        {_s('extendButton')}
                      </span>
                      <span
                        className={fetchingExtendedPlaces ? 'visible' : 'invisible'}
                        aria-label={_s('extendButtonLoading')}
                        style={{ gridArea: 'stack' }}>
                        <LoadingIcon style={{ height: 24, width: 24, margin: 'auto' }} />
                      </span>
                    </span>
                  </Button>
                </div>
              )}
              {results > SEARCH_RESULT_PER_PAGE_LIMIT && (
                <Pagination
                  currentlyShowing={{
                    start: page * SEARCH_RESULT_PER_PAGE_LIMIT + 1,
                    end:
                      page * SEARCH_RESULT_PER_PAGE_LIMIT + SEARCH_RESULT_PER_PAGE_LIMIT > results
                        ? results + (premiumProfilePlaces.length || 0)
                        : page * SEARCH_RESULT_PER_PAGE_LIMIT + SEARCH_RESULT_PER_PAGE_LIMIT,
                  }}
                  totalPages={Math.ceil(results / SEARCH_RESULT_PER_PAGE_LIMIT)}
                  nextPage={Boolean(hasNextPageResults) ? handleNextPageClick : undefined}
                  prevPage={Boolean(page > 0) ? handlePrevPageClick : undefined}
                  totalResults={results + (premiumProfilePlaces.length || 0)}
                  page={page}
                />
              )}
              <Footer />
            </div>
          </div>
        </div>
        {!isMobileView && (
          <div className={classnames('flex-1', MAP_BASIS)}>
            <MapTiler {...mapProps} />
          </div>
        )}
      </div>
    ),
    map: <MapTiler {...mapProps} columnClass="full" />,
  };

  return (
    <div className="bg-surface_l4">
      <NavBar
        source="list"
        navClass="list-container navigation"
        defaultNavBarHeight={isMobileView ? 120 : 128}
        prepend={prepend ? { element: prepend } : undefined}
        view={viewToggle.view}
        headerSlot={
          <div className="pb-sm">
            <div className="px-lg w-full xl:w-8/12">
              <SearchForm
                searchManager={managerWithPending}
                keywordAutoSuggestManager={keywordAutoSuggestManager}
                locationAutoSuggestManager={locationAutoSuggestManager}
                dateTimePickerManager={dateTimePickerManager}
              />
            </div>
            <div
              className={classnames(
                'pl-lg mt-sm space-x-sm flex',
                isMobileView && 'no-scrollbar flex overflow-x-scroll',
              )}>
              <SearchControls {...searchControlsProps} />
              {isMobileView && (
                <>
                  {viewToggle.render}
                  {/* just a spacer for horizontal scroll */}
                  <div className="pl-sm"></div>
                </>
              )}
            </div>
          </div>
        }
        middleSlot={
          <SearchForm
            searchManager={managerWithPending}
            keywordAutoSuggestManager={keywordAutoSuggestManager}
            locationAutoSuggestManager={locationAutoSuggestManager}
            dateTimePickerManager={dateTimePickerManager}
          />
        }
        bottomSlot={
          <Fragment>
            {viewToggle.view === 'list' &&
              (() => {
                if (searchControlsIntersectionObserver === 'aboveViewport' && isMobileView) {
                  return (
                    <div className="space-x-sm no-scrollbar pb-sm pt-xxs flex overflow-x-scroll">
                      <SearchControls {...searchControlsProps} />
                      {isMobileView && (
                        <>
                          {viewToggle.render}
                          {/* just a spacer for horizontal scroll */}
                          <div className="pl-sm"></div>
                        </>
                      )}
                    </div>
                  );
                }

                const megaMenu = (
                  <MegaMenu
                    {...megaMenuManager}
                    renderNavigationContainer={(props) => {
                      const ref = props.navigationContainerRef.current;
                      return ref ? (
                        createPortal(<MegaMenu.Categories {...props} />, ref)
                      ) : (
                        /**
                         * need to render the navigation before the ref is mounted to make sure navigation appears on initial render
                         */
                        <div
                          id={props.navigationContainerId}
                          className="mega-menu-navigation-container"
                          role="navigation">
                          <MegaMenu.Categories {...props} />
                        </div>
                      );
                    }}
                  />
                );

                if (megaMenuManager.activeTab) {
                  return megaMenu;
                }
                /** if ref is not mounted use 56px for now for for Mega menu tabs (update if component changes) */
                const height = megaMenuManager.navRef.current?.getBoundingClientRect()?.height ?? 56;
                return <HideOnScrollWrapper height={height}>{megaMenu}</HideOnScrollWrapper>;
              })()}
            {viewToggle.view === 'map' && (
              <div className="space-x-sm no-scrollbar pb-sm pt-xxs flex overflow-x-scroll">
                <SearchControls {...searchControlsProps} />
                {isMobileView && (
                  <>
                    {viewToggle.render}
                    {/* just a spacer for horizontal scroll */}
                    <div className="pl-sm"></div>
                  </>
                )}
              </div>
            )}
          </Fragment>
        }
        floatingSlot={<Fragment>{megaMenuManager.renderNavigationContainer(megaMenuManager)}</Fragment>}
      />

      {/* Content view */}
      {View[viewToggle.view]}

      <BookAppointmentModal />

      {showUserLocationOnboarding && (
        <UserLocationOnboarding
          isOpen={showUserLocationOnboarding}
          onClose={() => dispatch(modalActions.userLocationOnboarding({ show: false }))}
          onRequestUserLocation={handleOnRequestUserLocation}
        />
      )}
      <SEO {...searchManager} />
      {isMobileView && !showNavigationDrawer && <NavigationDrawer />}
      {isMobileView && showNavigationDrawer && <BottomNavBar source="list" />}
    </div>
  );
};

export default SearchResultsPage;
